import React, { useEffect, useState } from 'react';
import apiService from '../../../services/apiService';
import CustomerMessages from './CustomerMessages';
import CarSelector from './CarSelector';
import { renderCustomerNameWithTravels, parseCustomerNameParts } from '../../../utils/nameFormatter';

const WORKFLOW_STEPS = [
  'Booking',
  'Document Complete',
  'Loan Login',
  'Verification',
  'Account Opening',
  'Agreement',
  'Down Payment',
  'Disbursement',
  'RTO (Passing)',
  'Delivery'
];

const STANDARD_TEMPLATES: Record<string, string[]> = {
  'Booking': [
    'Collect booking amount receipt',
    'Verify customer photo & details',
    'Generate Booking Order form'
  ],
  'Document Complete': [
    'PAN CARD',
    'AADHAR CARD',
    'UDYAM CERTIFICATE',
    'DRIVING LICENSE',
    'LIGHT BILL',
    'RENT AGREEMENT',
    'ITR',
    '13 MONTH BANK STATEMENT',
    '8 PHOTO',
    '6 CHEQUE ( FOR BANK SECURITY )'
  ],
  'Loan Login': [
    'Submit files to bank partner',
    'Verify CIBIL score status',
    'Obtain Loan Sanction Letter'
  ],
  'Verification': [
    'Schedule physical home visit',
    'Schedule physical office visit',
    'Upload verification report'
  ],
  'Account Opening': [
    'Fill account opening form',
    'Complete customer KYC verification',
    'Issue welcome kit / checkbook'
  ],
  'Agreement': [
    'Draft loan agreement copy',
    'Get signatures from customer & bank',
    'Stamp paper execution check'
  ],
  'Down Payment': [
    'Receive down payment clear receipt',
    'Confirm receipt with accounts desk',
    'Generate tax invoice copy'
  ],
  'Disbursement': [
    'Submit delivery challan to bank',
    'Confirm disbursement payment credited',
    'Issue gate pass clearance'
  ],
  'RTO (Passing)': [
    'Submit tax and registration form',
    'Arrange physical passing inspection',
    'Fit High-Security Registration Plate (HSRP)'
  ],
  'Delivery': [
    'Arrange final vehicle polish & wash',
    'Conduct vehicle delivery ceremony',
    'Collect signed Delivery Acknowledgement form'
  ]
};

interface CustomersWorkflowProps {
  userRole: string;
}

const CustomersWorkflow: React.FC<CustomersWorkflowProps> = ({ userRole }) => {
  const [customers, setCustomers] = useState<any[]>([]);
  const [myWorkflowStep, setMyWorkflowStep] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedStep, setSelectedStep] = useState<any>(null);
  const [statusUpdate, setStatusUpdate] = useState({ status: '', reason: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ customer_name: '', phone: '', car_details: '' });
  const [editingCustomer, setEditingCustomer] = useState<any>(null);
  const [editingCustomerNameModal, setEditingCustomerNameModal] = useState<{ id: string; mainName: string; travelsName: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [selectedCustomerMessages, setSelectedCustomerMessages] = useState<any>(null);

  const handleSaveCustomerName = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCustomerNameModal) return;

    const main = editingCustomerNameModal.mainName.trim();
    const travels = editingCustomerNameModal.travelsName.trim();
    const full = travels ? `${main} (${travels})` : main;

    if (!main) {
      alert('Customer name cannot be empty');
      return;
    }

    setSaving(true);
    try {
      const res = await apiService.updateCrmCustomer(editingCustomerNameModal.id, {
        customer_name: full,
        performed_by: userId
      });
      if (res.success) {
        setEditingCustomerNameModal(null);
        fetchCustomers(true);
      } else {
        alert(res.message || 'Failed to update customer name');
      }
    } catch (err: any) {
      alert(err.message || 'Error updating customer name');
    } finally {
      setSaving(false);
    }
  };

  const [checklists, setChecklists] = useState<any[]>([]);
  const [newChecklistTask, setNewChecklistTask] = useState('');
  
  // Search and filter states
  const [search, setSearch] = useState('');
  const [filterStage, setFilterStage] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  const isAdmin = userRole === 'Admin' || userRole === 'Branch Manager';
  const isSales = userRole === 'Sales Team';
  const userStr = sessionStorage.getItem('aswCrmUser') || sessionStorage.getItem('aswUser') || localStorage.getItem('aswCrmUser') || localStorage.getItem('aswUser');
  const userId = userStr ? JSON.parse(userStr).id : null;

  useEffect(() => {
    const initialize = async () => {
      try {
        // Fetch this user's workflow step from the roles DB
        const rolesRes = await apiService.getCrmRoles();
        if (rolesRes.success) {
          const matched = rolesRes.data.find((r: any) => r.name === userRole);
          setMyWorkflowStep(matched?.workflow_step || null);
        }
      } catch (e) { console.error(e); }
      await fetchCustomers();
    };
    initialize();
  }, [userRole]);

  // Load checklists when selectedStep opens
  useEffect(() => {
    if (selectedStep) {
      apiService.getStepChecklist(selectedStep.id)
        .then((res: any) => {
          if (res.success) {
            const items = res.data || [];
            setChecklists(items);
            // If Document Complete step and no checklist items exist yet, auto-load standard 10 document template
            if (items.length === 0 && selectedStep.step_name === 'Document Complete') {
              const defaultTasks = STANDARD_TEMPLATES['Document Complete'];
              Promise.all(defaultTasks.map(t => apiService.addChecklistItem(selectedStep.id, t)))
                .then((results) => {
                  const added = results.filter((r: any) => r.success).map((r: any) => r.data);
                  setChecklists(added);
                })
                .catch((err: any) => console.error('Error auto-initializing documents:', err));
            }
          }
        })
        .catch((err: any) => console.error(err));
    } else {
      setChecklists([]);
    }
  }, [selectedStep]);

  const handleToggleChecklist = async (id: string, currentVal: boolean) => {
    try {
      const res = await apiService.toggleChecklistItem(id, !currentVal);
      if (res.success) {
        setChecklists(prev => prev.map(item => item.id === id ? { ...item, is_completed: !currentVal } : item));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddChecklist = async () => {
    if (!newChecklistTask.trim() || !selectedStep) return;
    try {
      const res = await apiService.addChecklistItem(selectedStep.id, newChecklistTask.trim());
      if (res.success) {
        setChecklists(prev => [...prev, res.data]);
        setNewChecklistTask('');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCustomers = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await apiService.getCrmCustomers();
      if (res.success) setCustomers(res.data);
    } catch (err) { console.error(err); }
    finally { if (!silent) setLoading(false); }
  };

  // Helper to find the first step that is not Done for a customer
  const getCustomerCurrentStep = (c: any) => {
    const steps = c.crm_workflow_steps || [];
    for (const stepName of WORKFLOW_STEPS) {
      const step = steps.find((s: any) => s.step_name === stepName);
      if (step && step.status !== 'Done') {
        return step;
      }
    }
    const lastStep = steps.find((s: any) => s.step_name === WORKFLOW_STEPS[WORKFLOW_STEPS.length - 1]);
    return lastStep || null;
  };

  // Filtered customer list
  const filteredCustomers = customers.filter(c => {
    if (search) {
      const q = search.toLowerCase();
      if (!c.customer_name?.toLowerCase().includes(q) && !c.phone?.includes(q) && !c.car_details?.toLowerCase().includes(q)) {
        return false;
      }
    }
    const steps = c.crm_workflow_steps || [];
    const currentStep = getCustomerCurrentStep(c);

    if (filterStage !== 'all' && filterStatus !== 'all') {
      const matchedStep = steps.find((s: any) => s.step_name === filterStage);
      if (!matchedStep || matchedStep.status !== filterStatus) return false;
    } else if (filterStage !== 'all') {
      if (currentStep?.step_name !== filterStage) {
        const matchedStep = steps.find((s: any) => s.step_name === filterStage);
        if (!matchedStep || (matchedStep.status === 'Done' && steps.some((s: any) => s.status !== 'Done'))) return false;
      }
    } else if (filterStatus !== 'all') {
      if (currentStep?.status !== filterStatus) {
        const hasStepWithStatus = steps.some((s: any) => s.status === filterStatus);
        if (!hasStepWithStatus) return false;
      }
    }
    return true;
  });

  const handleLoadTemplate = async () => {
    if (!selectedStep) return;
    const tasks = STANDARD_TEMPLATES[selectedStep.step_name] || [];
    if (tasks.length === 0) return;

    setLoadingTemplate(true);
    try {
      const promises = tasks.map(t => apiService.addChecklistItem(selectedStep.id, t));
      const results = await Promise.all(promises);
      const addedItems = results.filter(r => r.success).map(r => r.data);
      setChecklists(prev => [...prev, ...addedItems]);
    } catch (e) {
      console.error('Error loading default templates', e);
    } finally {
      setLoadingTemplate(false);
    }
  };

  // A user can edit a step if they are Admin or if that step matches their role's workflow_step
  const hasPermission = (stepName: string) => {
    if (isAdmin) return true;
    return myWorkflowStep === stepName;
  };

  const handleUpdateStep = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if checklist items are incomplete before setting to Done
    const incompleteChecklists = checklists.filter(item => !item.is_completed);
    if (statusUpdate.status === 'Done' && incompleteChecklists.length > 0) {
      const confirmMsg = `There are ${incompleteChecklists.length} incomplete sub-tasks in this stage. Are you sure you want to mark this stage as Done?`;
      if (!window.confirm(confirmMsg)) return;
    }

    const currentScroll = window.scrollY || document.documentElement.scrollTop;
    setSaving(true);
    try {
      await apiService.updateCrmWorkflowStep(selectedStep.id, {
        status: statusUpdate.status,
        reason: statusUpdate.reason,
        updated_by: userId
      });

      // Post the reason to the customer's message thread
      const currCustomer = customers.find(c => (c.crm_workflow_steps || []).some((s: any) => s.id === selectedStep.id));
      
      if (currCustomer) {
        if (statusUpdate.status === 'Cancelled') {
          apiService.updateCrmCustomer(currCustomer.id, {
            status: 'Booking Cancelled',
            performed_by: userId
          }).catch(() => {});
        }

        if (statusUpdate.reason.trim()) {
          apiService.logCrmActivity({
            action_type: statusUpdate.status === 'Cancelled' ? 'hold_reason' : (isAdmin ? 'admin_instruct' : 'staff_update'),
            entity_type: 'customer',
            entity_id: currCustomer.id,
            description: `[${selectedStep.step_name}] ${statusUpdate.status === 'Cancelled' ? '❌ Booking Cancelled: ' : ''}${statusUpdate.reason}`,
            performed_by: userId
          }).catch(() => {});
        } else {
          // Log a quiet system note if no text reason was provided but status changed
          apiService.logCrmActivity({
            action_type: 'status_change',
            entity_type: 'customer',
            entity_id: currCustomer.id,
            description: `Marked step "${selectedStep.step_name}" as ${statusUpdate.status}`,
            performed_by: userId
          }).catch(() => {});
        }
      }

      // Optimistic in-place update for instant UI feedback without page jump
      setCustomers(prev => prev.map(c => {
        const steps = c.crm_workflow_steps || [];
        if (steps.some((s: any) => s.id === selectedStep.id)) {
          return {
            ...c,
            crm_workflow_steps: steps.map((s: any) => s.id === selectedStep.id ? {
              ...s,
              status: statusUpdate.status,
              reason: statusUpdate.reason,
              updated_at: new Date().toISOString()
            } : s)
          };
        }
        return c;
      }));

      setSelectedStep(null);
      await fetchCustomers(true);

      // Preserve exact scroll position
      requestAnimationFrame(() => {
        window.scrollTo({ top: currentScroll, behavior: 'instant' as ScrollBehavior });
      });
    } catch { alert('Error updating step'); }
    finally { setSaving(false); }
  };

  const handleCreateDirectCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiService.createCrmCustomer({ ...newCustomer, performed_by: userId });
      setShowAddForm(false);
      setNewCustomer({ customer_name: '', phone: '', car_details: '' });
      fetchCustomers();
    } catch { alert('Error creating customer'); }
    finally { setSaving(false); }
  };

  const handleUpdateCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await apiService.updateCrmCustomer(editingCustomer.id, {
        customer_name: editingCustomer.customer_name,
        phone: editingCustomer.phone,
        car_details: editingCustomer.car_details,
        performed_by: userId
      });
      if (res.success) {
        setEditingCustomer(null);
        fetchCustomers();
      }
    } catch { alert('Error updating customer'); }
    finally { setSaving(false); }
  };

  const handleDeleteCustomer = async (id: string, name: string) => {
    if (!window.confirm(`WARNING: Are you sure you want to completely delete customer "${name}"?\n\nThis will permanently destroy their workflow history and activity logs.`)) return;
    
    try {
      const res = await apiService.deleteCrmCustomer(id);
      if (res.success) fetchCustomers(true);
    } catch { alert('Error deleting customer'); }
  };

  const stepStatusColor: Record<string, string> = {
    'Done':      'var(--crm-success)',
    'Hold':      'var(--crm-danger)',
    'Pending':   'var(--crm-warning)',
    'Cancelled': '#DC2626',
  };

  if (loading) {
    return (
      <div className="crm-loading">
        <div className="crm-spinner"></div>
        Loading customers...
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="crm-page-header">
        <div className="crm-page-header-left">
          <h2>Customers Workflow</h2>
          <p>
            {isAdmin
              ? 'Full visibility — click any step to update it'
              : `You can update: ${myWorkflowStep || '(no step assigned to your role)'}`}
          </p>
        </div>
        {(isAdmin || isSales) && (
          <button className="crm-btn crm-btn-primary" onClick={() => setShowAddForm(v => !v)}>
            {showAddForm ? '✕ Cancel' : '+ Direct Customer'}
          </button>
        )}
      </div>

      {/* Add Direct Customer Form */}
      {showAddForm && (
        <form className="crm-form-panel" onSubmit={handleCreateDirectCustomer}>
          <h3>➕ Add Walk-in / Direct Customer</h3>
          <div className="crm-form-fields">
            <div className="crm-form-field">
              <label>Customer Name *</label>
              <input type="text" placeholder="Full Name" required
                value={newCustomer.customer_name}
                onChange={e => setNewCustomer({ ...newCustomer, customer_name: e.target.value })} />
            </div>
            <div className="crm-form-field">
              <label>Phone Number *</label>
              <input type="text" placeholder="+91 98765…" required
                value={newCustomer.phone}
                onChange={e => setNewCustomer({ ...newCustomer, phone: e.target.value })} />
            </div>
            <div className="crm-form-field" style={{ gridColumn: '1 / -1' }}>
              <CarSelector
                value={newCustomer.car_details}
                onChange={(val) => setNewCustomer({ ...newCustomer, car_details: val })}
              />
            </div>
          </div>
          <div className="crm-form-actions">
            <button type="submit" className="crm-btn crm-btn-success" disabled={saving}>
              {saving ? 'Creating…' : '✓ Create & Start Workflow'}
            </button>
          </div>
        </form>
      )}

      {/* Edit Customer Form */}
      {editingCustomer && (
        <form className="crm-form-panel" style={{ border: '2px solid var(--crm-primary-light)' }} onSubmit={handleUpdateCustomer}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0 }}>✏️ Edit Customer: {editingCustomer.customer_name}</h3>
            <button type="button" onClick={() => setEditingCustomer(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}>✕</button>
          </div>
          <div className="crm-form-fields">
            <div className="crm-form-field">
              <label>Customer Name *</label>
              <input type="text" required
                value={editingCustomer.customer_name || ''}
                onChange={e => setEditingCustomer({ ...editingCustomer, customer_name: e.target.value })} />
            </div>
            <div className="crm-form-field">
              <label>Phone Number *</label>
              <input type="text" required
                value={editingCustomer.phone || ''}
                onChange={e => setEditingCustomer({ ...editingCustomer, phone: e.target.value })} />
            </div>
            <div className="crm-form-field" style={{ gridColumn: '1 / -1' }}>
              <CarSelector
                value={editingCustomer.car_details}
                onChange={(val) => setEditingCustomer({ ...editingCustomer, car_details: val })}
              />
            </div>
          </div>
          <div className="crm-form-actions" style={{ marginTop: 20 }}>
            <button type="button" className="crm-btn crm-btn-ghost" onClick={() => setEditingCustomer(null)}>Cancel</button>
            <button type="submit" className="crm-btn crm-btn-success" disabled={saving}>{saving ? 'Saving…' : '✓ Save Changes'}</button>
          </div>
        </form>
      )}
      {/* Search & Filter Toolbar */}
      <div style={{
        display: 'flex',
        gap: '10px',
        flexWrap: 'wrap',
        background: 'white',
        padding: '12px 16px',
        borderRadius: '8px',
        border: '1px solid var(--crm-border)',
        marginBottom: '16px',
        alignItems: 'center'
      }}>
        {/* Search Input */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          background: '#F8FAFC', 
          border: '1px solid var(--crm-border)', 
          borderRadius: '6px', 
          padding: '0 10px', 
          flex: '1 1 200px', 
          minWidth: '180px' 
        }}>
          <span style={{ marginRight: '6px', color: 'var(--crm-text-muted)', fontSize: '13px' }}>🔍</span>
          <input
            type="text"
            placeholder="Search by name or phone..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ 
              flex: 1, 
              border: 'none', 
              outline: 'none', 
              fontSize: '13px', 
              padding: '8px 0', 
              background: 'transparent',
              color: 'var(--crm-text-primary)'
            }}
          />
          {search && (
            <button 
              onClick={() => setSearch('')} 
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--crm-text-muted)', fontSize: '14px' }}
            >
              ×
            </button>
          )}
        </div>

        {/* Stage Filter */}
        <select
          value={filterStage}
          onChange={e => setFilterStage(e.target.value)}
          style={{
            padding: '8.5px 12px',
            fontSize: '13px',
            borderRadius: '6px',
            border: '1px solid var(--crm-border)',
            background: 'white',
            color: 'var(--crm-text-primary)',
            cursor: 'pointer',
            outline: 'none',
            minWidth: '150px'
          }}
        >
          <option value="all">All Stages</option>
          {WORKFLOW_STEPS.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        {/* Status Filter */}
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          style={{
            padding: '8.5px 12px',
            fontSize: '13px',
            borderRadius: '6px',
            border: '1px solid var(--crm-border)',
            background: 'white',
            color: 'var(--crm-text-primary)',
            cursor: 'pointer',
            outline: 'none',
            minWidth: '120px'
          }}
        >
          <option value="all">All Statuses</option>
          <option value="Pending">⏳ Pending / Active</option>
          <option value="Hold">🔴 On Hold</option>
          <option value="Done">✅ Completed</option>
          <option value="Cancelled">❌ Booking Cancelled</option>
        </select>

        {/* Clear button */}
        {(search || filterStage !== 'all' || filterStatus !== 'all') && (
          <button
            onClick={() => { setSearch(''); setFilterStage('all'); setFilterStatus('all'); }}
            style={{
              padding: '8px 12px',
              fontSize: '13px',
              fontWeight: 600,
              background: '#F1F5F9',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              color: 'var(--crm-text-secondary)',
              transition: 'background 0.15s'
            }}
          >
            Clear Filters
          </button>
        )}

        <div style={{ marginLeft: 'auto', fontSize: '13px', color: 'var(--crm-text-muted)', fontWeight: 500 }}>
          {filteredCustomers.length} customer{filteredCustomers.length !== 1 ? 's' : ''} found
        </div>
      </div>

      {/* Empty State for Filters */}
      {customers.length > 0 && filteredCustomers.length === 0 ? (
        <div className="crm-empty-state" style={{ padding: '40px 20px', background: 'white', border: '1px solid var(--crm-border)', borderRadius: '8px' }}>
          <h3>No matching customers</h3>
          <p>Try clearing or adjusting your search filters.</p>
          <button className="crm-btn crm-btn-ghost" style={{ marginTop: 12 }} onClick={() => { setSearch(''); setFilterStage('all'); setFilterStatus('all'); }}>
            Reset Filters
          </button>
        </div>
      ) : customers.length === 0 ? (
        <div className="crm-empty-state">
          <div className="crm-empty-state-icon">🚗</div>
          <h3>No customers yet</h3>
          <p>Convert a lead or add a walk-in customer to get started.</p>
        </div>
      ) : (
        <div className="crm-customers-list">
          {filteredCustomers.map((customer: any) => {
            const initial = customer.customer_name?.charAt(0)?.toUpperCase() || '?';
            const completedSteps = (customer.crm_workflow_steps || []).filter((s: any) => s.status === 'Done').length;
            const totalSteps = (customer.crm_workflow_steps || []).length;
            const pct = totalSteps ? Math.round((completedSteps / totalSteps) * 100) : 0;

            // Generate tracking link
            const trackLink = `${window.location.origin}/track/${customer.tracking_token || customer.id}`;

            return (
              <div className="crm-customer-card" key={customer.id}>
                <div className="crm-customer-card-header">
                  <div className="crm-customer-info">
                    <div className="crm-customer-avatar">{initial}</div>
                    <div>
                      <div className="crm-customer-name" style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        {renderCustomerNameWithTravels(customer.customer_name)}
                        <button
                          type="button"
                          onClick={() => {
                            const { mainName, travelsName } = parseCustomerNameParts(customer.customer_name || '');
                            setEditingCustomerNameModal({ id: customer.id, mainName, travelsName });
                          }}
                          style={{
                            background: '#F1F5F9',
                            border: '1px solid #CBD5E1',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '11px',
                            color: '#475569',
                            padding: '1px 5px',
                            fontWeight: 600,
                            lineHeight: 1
                          }}
                          title="Edit Customer Naming / Tours & Travels Name"
                        >
                          ✏️ Edit
                        </button>
                      </div>
                      <div className="crm-customer-phone">📞 {customer.phone}</div>
                      {customer.car_details && (
                        <div style={{ fontSize: '11.5px', color: 'var(--crm-text-muted)', marginTop: 2 }}>🚗 {customer.car_details}</div>
                      )}
                      <div style={{ marginTop: 8 }}>
                        <span style={{ fontSize: '11px', background: '#F1F5F9', color: '#475569', padding: '3px 8px', borderRadius: '4px', marginRight: '6px' }}>
                          ID: {customer.id.slice(0, 8)}
                        </span>
                        <a 
                          href={trackLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: '11px', color: 'var(--crm-primary)', textDecoration: 'none', fontWeight: 600 }}
                          title="Open public tracking link"
                        >
                          🔗 Track Link ➔
                        </a>
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <div style={{ fontSize: '12px', color: 'var(--crm-text-muted)', marginBottom: 4 }}>Progress</div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--crm-primary)' }}>{completedSteps}/{totalSteps} steps ({pct}%)</div>
                    {/* Progress bar */}
                    <div style={{ width: 80, height: 4, background: 'var(--crm-border)', borderRadius: 2, marginTop: 4, marginBottom: 12 }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? 'var(--crm-success)' : 'var(--crm-primary)', borderRadius: 2, transition: 'width 0.3s' }} />
                    </div>
                    
                    {/* Messages button with indicators */}
                    {(() => {
                      const hasHold = (customer.crm_workflow_steps || []).some((s: any) => s.status === 'Hold');
                      const hasNote = (customer.crm_workflow_steps || []).some((s: any) => s.reason);
                      return (
                        <button 
                          onClick={() => setSelectedCustomerMessages(customer)}
                          style={{ 
                            position: 'relative',
                            padding: '6px 12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                            background: hasHold ? '#FEF2F2' : hasNote ? '#FFFBEB' : 'var(--crm-primary-light)', 
                            color: hasHold ? '#991B1B' : hasNote ? '#92400E' : 'var(--crm-primary)', 
                            border: `1.5px solid ${hasHold ? '#FECACA' : hasNote ? '#FDE68A' : 'var(--crm-primary)'}`,
                            borderRadius: '8px',
                            transition: 'all 0.2s'
                          }}
                          title={hasHold ? 'Has steps on Hold — click to view details' : 'View / add notes for this customer'}
                        >
                          {hasHold ? '🚨 On Hold' : hasNote ? '💬 Notes' : '💬 Messages'}
                          {hasHold && (
                            <span style={{ 
                              position: 'absolute', top: -5, right: -5,
                              background: '#EF4444', color: 'white',
                              width: 16, height: 16, borderRadius: '50%',
                              fontSize: '9px', fontWeight: 800,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              border: '2px solid white'
                            }}>!</span>
                          )}
                        </button>
                      );
                    })()}

                    {/* Admin Actions */}
                    {isAdmin && (
                      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                        <button 
                          onClick={() => {
                            setEditingCustomer({ ...customer });
                            setShowAddForm(false);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className="crm-btn crm-btn-ghost" 
                          style={{ padding: '2px 6px', fontSize: '11px', color: 'var(--crm-text-secondary)' }}
                          title="Edit Customer Details"
                        >
                          ✏️ Edit
                        </button>
                        <button 
                          onClick={() => handleDeleteCustomer(customer.id, customer.customer_name)}
                          className="crm-btn crm-btn-ghost" 
                          style={{ padding: '2px 6px', fontSize: '11px', color: 'var(--crm-danger)' }}
                          title="Delete Customer Permanently"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="crm-workflow-steps">
                  {(() => {
                    const stepsList = customer.crm_workflow_steps || [];
                    const sortedSteps = [...stepsList].sort((a: any, b: any) => {
                      return WORKFLOW_STEPS.indexOf(a.step_name) - WORKFLOW_STEPS.indexOf(b.step_name);
                    });

                    // Find current active step index (first step that is NOT Done)
                    let activeStepIndex = WORKFLOW_STEPS.length;
                    for (let i = 0; i < WORKFLOW_STEPS.length; i++) {
                      const stepObj = sortedSteps.find((s: any) => s.step_name === WORKFLOW_STEPS[i]);
                      if (stepObj && stepObj.status !== 'Done') {
                        activeStepIndex = i;
                        break;
                      }
                    }

                    return sortedSteps.map((step: any) => {
                      const stepIndex = WORKFLOW_STEPS.indexOf(step.step_name);
                      const isFuture = stepIndex > activeStepIndex;
                      const isCurrent = stepIndex === activeStepIndex;
                      
                      const editable = isAdmin ? !isFuture : (myWorkflowStep === step.step_name && isCurrent);

                      let statusLabel = step.status;
                      let badgeClass = step.status.toLowerCase();
                      let borderColor = stepStatusColor[step.status] || 'var(--crm-border)';
                      
                      if (isFuture) {
                        statusLabel = 'Locked';
                        badgeClass = 'locked';
                        borderColor = '#e2e8f0';
                      } else if (isCurrent && step.status === 'Pending') {
                        statusLabel = 'Active';
                        badgeClass = 'active';
                      }

                      let durationText = '';
                      if (!isFuture && step.status !== 'Done') {
                        const stepTime = step.updated_at || customer.created_at;
                        const diffMs = Date.now() - new Date(stepTime).getTime();
                        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                        if (diffDays > 0) {
                          durationText = `${diffDays}d elapsed`;
                        } else if (diffHours > 0) {
                          durationText = `${diffHours}h elapsed`;
                        } else {
                          durationText = `just started`;
                        }
                      }

                      return (
                        <div
                          key={step.step_name}
                          className={`crm-workflow-step ${editable ? 'editable' : 'disabled'} ${isFuture ? 'future-step' : ''}`}
                          style={{ 
                            borderLeftWidth: '3px', 
                            borderLeftColor: borderColor,
                            backgroundColor: isFuture ? '#F8FAFC' : isCurrent ? '#F0F9FF' : 'white',
                            opacity: isFuture ? 0.6 : 1
                          }}
                          onClick={() => editable && (setSelectedStep(step), setStatusUpdate({ status: step.status, reason: step.reason || '' }))}
                          title={
                            isFuture 
                              ? `🔒 This stage is locked until previous stages are completed` 
                              : editable 
                              ? `Click to update ${step.step_name}` 
                              : `Only ${step.step_name} team can update this`
                          }
                        >
                          <div className="crm-step-name">{step.step_name}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                            <span className={`crm-status-badge status-${badgeClass}`}>
                              {statusLabel === 'Done' ? '✓ Done' : statusLabel === 'Active' ? '⏳ Active' : statusLabel === 'Locked' ? '🔒 Locked' : statusLabel}
                            </span>
                            {durationText && (
                              <span style={{ fontSize: '10.5px', color: '#64748B', fontWeight: 500 }} title="Time spent in current status">
                                ({durationText})
                              </span>
                            )}
                          </div>
                          {step.reason && !isFuture && <div className="crm-step-reason">⚠ {step.reason}</div>}
                          {step.updated_by?.name && !isFuture && <div className="crm-step-updater">by {step.updated_by.name}</div>}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Update Step Modal */}
      {selectedStep && (
        <div className="crm-modal-overlay" onClick={() => setSelectedStep(null)}>
          <div className="crm-modal" style={{ maxWidth: '500px', width: '90%' }} onClick={e => e.stopPropagation()}>
            <div className="crm-modal-header">
              <h3>Update: {selectedStep.step_name}</h3>
              <button className="crm-modal-close" onClick={() => setSelectedStep(null)}>✕</button>
            </div>
            <form onSubmit={handleUpdateStep}>
              <div className="crm-modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <div className="crm-modal-row">
                  <label>Status</label>
                  <select
                    value={statusUpdate.status}
                    onChange={e => setStatusUpdate({ ...statusUpdate, status: e.target.value })}
                  >
                    <option value="Pending">⏳ Pending</option>
                    <option value="Hold">🔴 Hold</option>
                    <option value="Done">✅ Done</option>
                    <option value="Cancelled">❌ Booking Cancelled</option>
                  </select>
                </div>
                {(statusUpdate.status === 'Hold' || statusUpdate.status === 'Pending' || statusUpdate.status === 'Cancelled') && (
                  <div className="crm-modal-row">
                    <label>{statusUpdate.status === 'Cancelled' ? 'Cancellation Reason' : 'Reason / Note'}</label>
                    <textarea
                      placeholder={statusUpdate.status === 'Cancelled' ? "Enter reason for booking cancellation..." : "Explain the hold or pending situation..."}
                      rows={3}
                      value={statusUpdate.reason}
                      onChange={e => setStatusUpdate({ ...statusUpdate, reason: e.target.value })}
                    />
                  </div>
                )}

                {/* Sub-Task Checklist */}
                <div style={{ marginTop: '18px', borderTop: '1px solid var(--crm-border)', paddingTop: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <label style={{ fontSize: '13px', fontWeight: 700, color: 'var(--crm-text-secondary)', display: 'block', margin: 0 }}>
                      {selectedStep.step_name === 'Document Complete' ? '📄 Mark/Tick Received Customer Documents (PAN, Aadhaar, Bank Statement, etc.)' : '📝 Stage Sub-Tasks Checklist'}
                    </label>
                    {checklists.length === 0 && (
                      <button
                        type="button"
                        onClick={handleLoadTemplate}
                        disabled={loadingTemplate}
                        style={{
                          fontSize: '11px',
                          color: '#4F46E5',
                          background: '#EEF2FF',
                          border: '1px solid #C7D2FE',
                          borderRadius: '4px',
                          padding: '3px 8px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          transition: 'all 0.15s'
                        }}
                        title="Automatically load default operational checklist tasks for this stage"
                      >
                        {loadingTemplate ? 'Loading...' : '⚡ Auto-Load Template'}
                      </button>
                    )}
                  </div>
                  
                  {checklists.length === 0 ? (
                    <p style={{ fontSize: '12px', color: 'var(--crm-text-muted)', margin: '0 0 12px' }}>
                      No sub-tasks yet. Click "Auto-Load Template" or add custom tasks below.
                    </p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px', background: '#F8FAFC', padding: '10px 12px', borderRadius: '8px', border: '1px solid #F1F5F9' }}>
                      {checklists.map((item) => (
                        <label key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', cursor: 'pointer', userSelect: 'none' }}>
                          <input
                            type="checkbox"
                            style={{ width: '15px', height: '15px', cursor: 'pointer' }}
                            checked={item.is_completed}
                            onChange={() => handleToggleChecklist(item.id, item.is_completed)}
                          />
                          <span style={{ 
                            textDecoration: item.is_completed ? 'line-through' : 'none', 
                            color: item.is_completed ? 'var(--crm-text-muted)' : 'var(--crm-text-primary)',
                            fontWeight: item.is_completed ? 400 : 500
                          }}>
                            {item.task_name}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '6px' }}>
                    <input
                      type="text"
                      placeholder="Add custom task..."
                      value={newChecklistTask}
                      onChange={e => setNewChecklistTask(e.target.value)}
                      style={{ 
                        flex: 1, 
                        padding: '7px 11px', 
                        fontSize: '12.5px', 
                        border: '1.5px solid var(--crm-border)', 
                        borderRadius: 'var(--crm-radius-sm)', 
                        outline: 'none',
                        background: 'white'
                      }}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddChecklist(); } }}
                    />
                    <button
                      type="button"
                      onClick={handleAddChecklist}
                      style={{ 
                        padding: '6px 14px', 
                        fontSize: '12.5px', 
                        background: 'var(--crm-primary)', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: 'var(--crm-radius-sm)', 
                        cursor: 'pointer',
                        fontWeight: 600
                      }}
                    >
                      + Add
                    </button>
                  </div>
                </div>
              </div>
              <div className="crm-modal-actions" style={{ borderTop: '1px solid var(--crm-border)', paddingTop: '14px', marginTop: '14px' }}>
                <button type="button" className="crm-btn crm-btn-ghost" onClick={() => setSelectedStep(null)}>Cancel</button>
                <button type="submit" className="crm-btn crm-btn-success" disabled={saving}>
                  {saving ? 'Saving…' : '✓ Update Step'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Messages Modal */}
      {selectedCustomerMessages && (
        <CustomerMessages 
          customer={selectedCustomerMessages} 
          userRole={userRole} 
          onClose={() => setSelectedCustomerMessages(null)} 
        />
      )}

      {/* Edit Customer Name Modal */}
      {editingCustomerNameModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: '20px'
        }}>
          <div style={{
            background: 'white', borderRadius: '16px', maxWidth: '440px', width: '100%',
            padding: '24px', boxShadow: '0 20px 40px rgba(0,0,0,0.25)', position: 'relative'
          }}>
            <button
              type="button"
              onClick={() => setEditingCustomerNameModal(null)}
              style={{
                position: 'absolute', top: '16px', right: '16px', background: '#F1F5F9',
                border: 'none', borderRadius: '50%', width: '30px', height: '30px',
                cursor: 'pointer', fontSize: '15px', color: '#64748B', display: 'flex',
                alignItems: 'center', justifyContent: 'center'
              }}
            >
              ✕
            </button>

            <div style={{ marginBottom: '18px' }}>
              <h3 style={{ margin: '0 0 4px 0', fontSize: '17px', color: '#1E293B', fontWeight: 800, fontFamily: 'Inter, sans-serif' }}>
                ✏️ Edit Customer Naming
              </h3>
              <p style={{ margin: 0, fontSize: '12.5px', color: '#64748B' }}>
                Update person name and add optional Tours & Travels name
              </p>
            </div>

            <form onSubmit={handleSaveCustomerName} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: 5 }}>
                  Customer / Person Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Rohit or Deepak Sharma"
                  value={editingCustomerNameModal.mainName}
                  onChange={e => setEditingCustomerNameModal({ ...editingCustomerNameModal, mainName: e.target.value })}
                  style={{
                    width: '100%', padding: '9px 12px', border: '1.5px solid #CBD5E1',
                    borderRadius: '8px', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: 5 }}>
                  Tours & Travels / Business Name (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. G A Tours or Royal Travels"
                  value={editingCustomerNameModal.travelsName}
                  onChange={e => setEditingCustomerNameModal({ ...editingCustomerNameModal, travelsName: e.target.value })}
                  style={{
                    width: '100%', padding: '9px 12px', border: '1.5px solid #CBD5E1',
                    borderRadius: '8px', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box'
                  }}
                />
                <div style={{ fontSize: '11.5px', color: '#0369A1', background: '#F0F9FF', border: '1px solid #BAE6FD', padding: '8px 10px', borderRadius: '6px', marginTop: '8px', lineHeight: '1.4' }}>
                  💡 <strong>Display Note:</strong> Tours & Travels name will be displayed in small font next to the customer name across all workflow screens.
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: '8px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setEditingCustomerNameModal(null)}
                  style={{
                    padding: '9px 16px', background: '#F1F5F9', color: '#475569',
                    border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '13px', cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    padding: '9px 20px', background: '#0284C7', color: 'white',
                    border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '13px', cursor: 'pointer'
                  }}
                >
                  {saving ? 'Saving...' : 'Save Naming'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomersWorkflow;
